/*@flow*/
import assert from './assert';
import isTypeName from './isTypeName';
import isFunction from './isFunction';
import isArray from './isArray';
import forbidNewOperator from './forbidNewOperator';
import is from './is';
import getTypeName from './getTypeName';
import isIdentity from './isIdentity';
import update from './update';
import type { TypeT } from './types';
import withOptions from './withOptions';

type IntersectionType = TypeT<{ types: TypeT<*>[] }>;

function getDefaultName(types: TypeT<*>[]): string {
  return types.map(getTypeName).join(' & ');
}

function intersection(types: TypeT<*>[], name?: string): IntersectionType {
  if (process.env.NODE_ENV !== 'production') {
    assert(
      isArray(types) && types.every(isFunction) && types.length >= 2,
      function() {
        return (
          'Invalid argument types ' +
          assert.stringify(types) +
          ' supplied to intersection(types, [name]) combinator (expected an array of at least 2 types)'
        );
      }
    );
    assert(isTypeName(name), function() {
      return (
        'Invalid argument name ' +
        assert.stringify(name) +
        ' supplied to intersection(types, [name]) combinator (expected a string)'
      );
    });
  }

  let displayName = name || getDefaultName(types);
  let identity = types.every(isIdentity);

  function Intersection(value: any, path?: string[]) {
    let path1 = path || [displayName];

    if (process.env.NODE_ENV !== 'production') {
      if (identity) {
        forbidNewOperator(this, Intersection);
      }
      assert(Intersection.is(value), function() {
        return (
          'Invalid value ' +
          assert.stringify(value) +
          ' supplied to ' +
          path1.join('/')
        );
      });
    }

    return value;
  }

  Intersection.meta = {
    kind: 'intersection',
    types,
    name,
    identity,
  };

  Intersection.displayName = displayName;

  Intersection.is = x => types.every(type => is(x, type));

  Intersection.update = (instance, patch) =>
    Intersection(update(instance, patch));

  withOptions(Intersection);

  return Intersection;
}

intersection.getDefaultName = getDefaultName;
export default intersection;
